import { memo } from 'react';

/**
 * SVG slice curve overlay — renders one row of a pre-computed 2D function array.
 * @param {{ axis, functionArray, paramY, color, dashArray, strokeWidth, isVertical, clippingRatio }} props
 */
function IntFigSlice({
  axis,
  functionArray,
  paramY,
  color = '#1f76D2',
  dashArray = '0 0',
  strokeWidth = 0.02,
  isVertical = false,
  clippingRatio = 1,
}) {
  const noSlices   = functionArray.length;
  const noSegments = functionArray[0]?.length ?? 0;

  const inc = (axis.max_x - axis.min_x) / noSegments;
  const xs  = Array.from({ length: noSegments + 1 }, (_, i) => i * inc / (axis.max_x - axis.min_x));

  const yIdx = Math.min(Math.max(Math.round(noSlices * paramY), 0), noSlices - 1);
  const row  = [...functionArray[yIdx], functionArray[yIdx][functionArray[yIdx].length - 1]];

  if (isVertical) {
    const ys = row.map(v => (v / clippingRatio - axis.min_y) / (axis.max_y - axis.min_y));
    return (
      <svg width="100%" height="100%" viewBox="0 0 1 1" preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0 }}>
        {ys.slice(0, -1).map((y, i) => {
          const lim = 1 / clippingRatio;
          if (ys[i] >= lim && ys[i + 1] >= lim) return null;
          const y1c = Math.min(ys[i],     lim);
          const y2c = Math.min(ys[i + 1], lim);
          return (
            <line key={i}
              x1={y1c} y1={xs[i]} x2={y2c} y2={xs[i + 1]}
              stroke={color} strokeWidth={strokeWidth}
              strokeLinecap="round" strokeLinejoin="round"
              strokeDasharray={dashArray}
            />
          );
        })}
      </svg>
    );
  }

  const ys = row.map(v => 1 - (Math.min(v / clippingRatio, axis.max_y) - axis.min_y) / (axis.max_y - axis.min_y));
  return (
    <svg width="100%" height="100%" viewBox="0 0 1 1" preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0 }}>
      {ys.slice(0, -1).map((y, i) => {
        const lim = 1 / clippingRatio;
        const iv1 = 1 - ys[i];
        const iv2 = 1 - ys[i + 1];
        if (iv1 >= lim && iv2 >= lim) return null;
        const y1c = iv1 >= lim ? 1 - lim : ys[i];
        const y2c = iv2 >= lim ? 1 - lim : ys[i + 1];
        return (
          <line key={i}
            x1={xs[i]} y1={y1c} x2={xs[i + 1]} y2={y2c}
            stroke={color} strokeWidth={strokeWidth}
            strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={dashArray}
          />
        );
      })}
    </svg>
  );
}

export default memo(IntFigSlice);
